import React, { useState, useEffect } from 'react';
import { useForm } from '../../../context/FormContext';
import FormField from '../../common/FormField/FormField';
import FileUpload from '../../common/FileUpload/FileUpload';
import { validateSection, isSectionComplete, validateFileUpload } from '../../../utils/validation';
import './Documentation.css';

const Documentation = ({ data, errors, onSave, isLoading, isReadOnly }) => {
  const { actions } = useForm();
  const [formData, setFormData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;

    // Validate file if it's a file upload
    let fileError = null;
    if (value && value.original_file) {
      fileError = validateFileUpload(field, value.original_file);
      if (fileError) {
        actions.setSectionErrors('documentation', { ...errors, [field]: fileError });
        return;
      }
    }

    const newData = { ...formData, [field]: value };
    setFormData(newData);
    setHasChanges(true);

    actions.updateSectionData('documentation', newData, 'in_progress');
  };

  const handleSave = () => {
    if (hasChanges) {
      // Validate the section
      const validationErrors = validateSection('documentation', formData);
      const isComplete = isSectionComplete('documentation', formData);

      // Set validation errors in context
      actions.setSectionErrors('documentation', validationErrors);

      // Determine status based on completion
      const status = isComplete ? 'completed' : 'in_progress';

      // Update section data with proper status
      actions.updateSectionData('documentation', formData, status);

      // Call the save handler
      onSave(formData);
      setHasChanges(false);
    }
  };

  // Removed auto-saving - users must manually save sections

  const requiredDocs = [
    { id: 'incorporationDocs', label: 'Incorporation documents', required: true },
    { id: 'memorandumArticles', label: 'Memorandum and Articles of Association', required: true },
    { id: 'certificateIncorporation', label: 'Certificate of Incorporation', required: true },
    { id: 'companyStructure', label: 'Company structure showing UBO (25%)', required: true },
    { id: 'signatoryList', label: 'List of signatories', required: true },
    { id: 'proofAddress', label: 'Proof of registered address', required: true },
    { id: 'regulatoryLicenses', label: 'Regulatory licenses (if applicable)', required: false },
    { id: 'additionalDocs', label: 'Additional supporting documentation', required: false }
  ];

  return (
    <div className="documentation">
      <div className="section-intro">
        <p>Please upload all required documents. Accepted formats: PDF, DOC, DOCX (max 10MB each).</p>
      </div>

      <div className="documents-grid">
        {requiredDocs.map((doc) => (
          <FormField
            key={doc.id}
            label={doc.label}
            required={doc.required}
            error={errors[doc.id]}
            className="document-field"
          >
            <FileUpload
              value={formData[doc.id]}
              onChange={(file) => handleChange(doc.id, file)}
              error={errors[doc.id]}
              disabled={isReadOnly}
              accept=".pdf,.doc,.docx"
              placeholder={`Upload ${doc.label.toLowerCase()}`}
              documentType={doc.id}
            />
          </FormField>
        ))}
      </div>

      {!isReadOnly && (
        <div className="section-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Section'}
          </button>

          {hasChanges && (
            <span className="unsaved-indicator">
              You have unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default Documentation;
